import { useEffect, useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useCacheStore } from '../db/bootstrap'
import { searchInstruments, upsertInstruments } from '../db/instruments'
import { searchInstrumentsRemote } from '../api/marketData'
import type { InstrumentDetail, InstrumentType } from '../api/types'

interface Options {
  type?: InstrumentType
  limit?: number
}

export interface UseInstrumentSearchResult {
  results: InstrumentDetail[]
  isLoading: boolean
  isSyncing: boolean
  error: Error | null
}

// The search box is not debounced — local SQLite eats a query per keystroke
// happily, but Schwab /instruments must not (≈120 req/min app-wide, shared
// with quote polling). Only the remote leg waits for typing to settle.
const REMOTE_DEBOUNCE_MS = 250

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

/**
 * Two-layer search: the local cache (SQLite/OPFS + memory) answers instantly
 * with anything previously searched, and a debounced Schwab search fills in
 * the rest. Remote results are written through to the local cache so repeat
 * searches resolve offline.
 */
export function useInstrumentSearch(prefix: string, opts: Options = {}): UseInstrumentSearchResult {
  const ready = useCacheStore((s) => s.ready)
  const initError = useCacheStore((s) => s.initError)
  const trimmed = prefix.trim().toUpperCase()
  const limit = opts.limit ?? 20
  const type = opts.type ?? null
  const debounced = useDebouncedValue(trimmed, REMOTE_DEBOUNCE_MS)

  const local = useQuery<InstrumentDetail[], Error>({
    // `ready` is in the key so results refresh once SQLite finishes booting.
    queryKey: ['instruments', 'search', 'local', trimmed, type, limit, ready],
    enabled: trimmed.length > 0,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: () => searchInstruments(trimmed, { type: type ?? undefined, limit }),
  })

  const remote = useQuery<InstrumentDetail[], Error>({
    queryKey: ['instruments', 'search', 'remote', debounced, limit],
    enabled: debounced.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await searchInstrumentsRemote(debounced, limit)
      // Write-through; don't block rendering on the SQLite insert.
      void upsertInstruments(res.instruments).catch(() => {})
      return res.instruments
    },
  })

  const results = useMemo(() => {
    const merged = new Map<string, InstrumentDetail>()
    for (const it of local.data ?? []) merged.set(it.instrument.symbol, it)
    // Only merge remote rows that answer the *current* input — while the
    // debounce lags, stale remote results would pollute the list.
    if (debounced === trimmed && trimmed.length > 0) {
      for (const it of remote.data ?? []) {
        if (type && it.instrument.type !== type) continue
        if (!merged.has(it.instrument.symbol)) merged.set(it.instrument.symbol, it)
      }
    }
    const out = [...merged.values()]
    out.sort((a, b) => {
      // Symbol-prefix matches first (description matches come from Schwab's
      // desc-search and don't start with the query), then shortest symbol.
      const pa = a.instrument.symbol.startsWith(trimmed) ? 0 : 1
      const pb = b.instrument.symbol.startsWith(trimmed) ? 0 : 1
      if (pa !== pb) return pa - pb
      const la = a.instrument.symbol.length
      const lb = b.instrument.symbol.length
      if (la !== lb) return la - lb
      return a.instrument.symbol.localeCompare(b.instrument.symbol)
    })
    return out.slice(0, limit)
  }, [local.data, remote.data, debounced, trimmed, type, limit])

  return {
    results,
    isLoading: trimmed.length > 0 && results.length === 0 && (local.isLoading || remote.isLoading),
    isSyncing: remote.isFetching,
    error: remote.error ?? local.error ?? (initError ? new Error(initError) : null),
  }
}
